import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import useI18n from "../../../hooks/useTranslations";
import { HrailTerminalType } from "../../../http/hrail/types/common";
import {
  HrailConfiguration,
  HrailConfigurationSegment,
  HrailCurveOrientation,
  HrailHeadingDegrees,
  HrailSegmentAngle,
  HrailTurnDirection,
  UpdateHrailConfigurationBody,
  UpsertHrailConfigurationSegmentBody,
} from "../../../http/hrail/types/configurations";
import { HrailFasteningOption } from "../../../http/hrail/types/fasteningOptions";
import { HrailSettings } from "../../../http/hrail/types/hrailSettings";
import { HrailLabelLanguage } from "../../../http/hrail/types/labelLanguages";
import { HrailSubstructure } from "../../../http/hrail/types/substructures";
import { HrailSystemCurveOption } from "../../../http/hrail/types/systemCurveOptions";
import { HrailSystem } from "../../../http/hrail/types/systems";
import { HrailSystemSupportOption } from "../../../http/hrail/types/systemSupportOptions";
import { HrailSystemTrolleyOption } from "../../../http/hrail/types/systemTrolleyOptions";
import { HrailSystemWaterproofKit } from "../../../http/hrail/types/systemWaterproofKits";
import { HrailWorkMethod } from "../../../http/hrail/types/workMethods";
import { useHrailConfigurationSegmentsApi } from "../../../http/hrail/useHrailConfigurationSegments";
import { useHrailSettings } from "../../../http/hrail/useHrailSettings";
import { UpdateConfigurationBody } from "../../../http/types/configurations";
import { ListItemWithQuantity } from "../../../http/types/items";
import { useConfigurationsApi } from "../../../http/useConfigurations";
import { useItemsApi } from "../../../http/useItems";
import SvgAdd from "../../icons/Add";
import SvgDelete from "../../icons/Delete";
import SvgSave from "../../icons/Save";
import Button from "../../ui/Button";
import ErrorText from "../../ui/ErrorText";
import FlexContent from "../../ui/FlexContent";
import FormField from "../../ui/FormField";
import Input from "../../ui/Input";
import LoadingSpinner from "../../ui/LoadingSpinner";
import { CustomSelect } from "../../ui/select/CustomSelect";
import SubmitBar from "../../ui/SubmitBar";
import Switch from "../../ui/Switch";
import Table from "../../ui/Table";
import Textarea from "../../ui/Textarea";
import styles from "./HRail.module.css";

interface HrailProps {
  configuration: HrailConfiguration;
  isAdmin?: boolean;
}

type HrailFormState = {
  name: string;
  description: string | null;
  systemId: string;
  substructureId: string;
  workMethodId: string | null;
  systemSupportOptionId: string | null;
  fasteningOptionId: string | null;
  systemTrolleyOptionId: string | null;
  systemWaterproofKitId: string | null;
  labelLanguageId: string | null;
  startTerminalType: HrailTerminalType;
  endTerminalType: HrailTerminalType;
  doubleTerminalType: HrailTerminalType | null;
  initialCurveOrientation: HrailCurveOrientation | null;
  jointHoleTemplateRequired: boolean;
  trolleyQuantity: number | null;
  labelQuantity: number;
};

type RouteRowMode = "direction" | "angle" | "terminal";

type DraftRouteRow = {
  key: string;
  id?: string;
  configurationId: string;
  mode: RouteRowMode;
  lengthM: number | "";
  headingDegrees: HrailHeadingDegrees | "";
};

type TerminalTypeOption = {
  value: HrailTerminalType;
  label: string;
};

type DerivedCurve = {
  endAngleDegrees: HrailSegmentAngle;
  turnDirection: HrailTurnDirection;
};

const routeRowModeOptions: { value: Exclude<RouteRowMode, "terminal">; label: string }[] = [
  { value: "direction", label: "Direction" },
  { value: "angle", label: "Custom angle" },
];
const cardinalHeadingOptions: HrailHeadingDegrees[] = [0, 90, 180, 270];
const supportedHeadingOptions: HrailHeadingDegrees[] = [
  0, 30, 45, 60, 90, 120, 135, 150, 180, 210, 225, 240, 270, 300, 315, 330,
];
const defaultTerminalTypeOptions: TerminalTypeOption[] = [
  { value: "closed", label: "RAILEND" },
  { value: "open", label: "RAILENDOPEN" },
];
const curveOrientationOptions: { value: HrailCurveOrientation; label: string }[] = [
  { value: "internal", label: "Internal" },
  { value: "external", label: "External" },
];
const doubleTerminalSupportCodes = new Set(["RAILBRAW", "RAILBRAS"]);

const itemLabel = (code?: string, description?: string) => [code, description].filter(Boolean).join(" - ");

const getWorkMethodOptions = (settings: HrailSettings, systemId: string): HrailWorkMethod[] => {
  const workMethodIds = new Set(
    settings.systemSupportOptions.filter((item) => item.system.id === systemId).map((item) => item.workMethod.id),
  );

  return settings.workMethods
    .filter((workMethod) => workMethodIds.has(workMethod.id))
    .sort((left, right) => left.order - right.order);
};

const getSubstructureOptions = (
  settings: HrailSettings,
  systemId: string,
  workMethodId: string | null,
): HrailSubstructure[] => {
  if (!workMethodId) {
    return [];
  }

  const substructureIds = new Set([
    ...settings.systemSupportOptions
      .filter((item) => item.system.id === systemId && item.workMethod.id === workMethodId)
      .map((item) => item.substructure.id),
    ...settings.fasteningOptions.filter((item) => item.system.id === systemId).map((item) => item.substructure.id),
  ]);

  return settings.substructures
    .filter((substructure) => substructureIds.has(substructure.id))
    .sort((left, right) => left.order - right.order);
};

const getSupportOptionOptions = (
  settings: HrailSettings,
  systemId: string,
  substructureId: string,
  workMethodId: string | null,
): HrailSystemSupportOption[] =>
  workMethodId
    ? settings.systemSupportOptions
        .filter(
          (item) =>
            item.system.id === systemId &&
            item.substructure.id === substructureId &&
            item.workMethod.id === workMethodId,
        )
        .sort((left, right) => left.order - right.order)
    : [];

const retainCompatibleSupportOptionId = (
  supportOptionId: string | null,
  supportOptions: HrailSystemSupportOption[],
): string | null =>
  supportOptionId !== null && supportOptions.some((option) => option.id === supportOptionId)
    ? supportOptionId
    : (supportOptions[0]?.id ?? null);

const getFasteningOptionOptions = (
  settings: HrailSettings,
  systemId: string,
  substructureId: string,
  supportId: string | null,
): HrailFasteningOption[] =>
  supportId
    ? settings.fasteningOptions.filter(
        (item) =>
          item.system.id === systemId && item.substructure.id === substructureId && item.support.id === supportId,
      )
    : [];

const getTrolleyOptionOptions = (settings: HrailSettings, systemId: string): HrailSystemTrolleyOption[] =>
  settings.systemTrolleyOptions
    .filter((item) => item.system.id === systemId)
    .sort((left, right) => left.order - right.order);

const getWaterproofKitOptions = (settings: HrailSettings, systemId: string): HrailSystemWaterproofKit[] =>
  settings.systemWaterproofKits.filter((item) => item.system.id === systemId);

const getSystemCurveOptions = (settings: HrailSettings, systemId: string): HrailSystemCurveOption[] =>
  settings.systemCurveOptions.filter((item) => item.system.id === systemId);

const getCurveDeflections = (curveOptions: HrailSystemCurveOption[]): number[] =>
  [...new Set(curveOptions.map((item) => item.curve.deflectionDegrees))]
    .filter((deflection) => deflection > 0)
    .sort((left, right) => left - right);

const getTerminalTypeOptions = (settings: HrailSettings): TerminalTypeOption[] => {
  const options = settings.railTerminals
    .map((item) => ({
      value: item.terminalType,
      label: item.item.code || item.terminalType,
    }))
    .filter((item, index, items) => items.findIndex((option) => option.value === item.value) === index);
  return options.length > 0 ? options : defaultTerminalTypeOptions;
};

const isHrailHeadingDegrees = (value: number): value is HrailHeadingDegrees =>
  supportedHeadingOptions.includes(value as HrailHeadingDegrees);

const isHrailSegmentAngle = (value: number): value is HrailSegmentAngle =>
  [90, 120, 135, 150].includes(value as HrailSegmentAngle);

const isCardinalHeading = (value: HrailHeadingDegrees | ""): value is HrailHeadingDegrees =>
  value !== "" && cardinalHeadingOptions.includes(value);

const normalizeHeading = (value: number): HrailHeadingDegrees | null => {
  const normalized = ((value % 360) + 360) % 360;
  return isHrailHeadingDegrees(normalized) ? normalized : null;
};

const shortestTurnDegrees = (from: HrailHeadingDegrees, to: HrailHeadingDegrees): number => {
  const clockwise = ((to - from + 540) % 360) - 180;
  return clockwise === -180 ? 180 : clockwise;
};

const headingLabel = (heading: HrailHeadingDegrees): string => {
  switch (heading) {
    case 0:
      return "Right (0 deg)";
    case 90:
      return "Up (90 deg)";
    case 180:
      return "Left (180 deg)";
    case 270:
      return "Down (270 deg)";
    default:
      return `${heading} deg`;
  }
};

const getFixedDirectionOptions = (
  previousHeading: HrailHeadingDegrees,
  availableDeflections: number[],
): HrailHeadingDegrees[] =>
  cardinalHeadingOptions.filter((heading) => {
    const magnitude = Math.abs(shortestTurnDegrees(previousHeading, heading));
    return heading !== previousHeading && availableDeflections.includes(magnitude);
  });

const getCustomAngleOptions = (
  previousHeading: HrailHeadingDegrees,
  availableDeflections: number[],
): HrailHeadingDegrees[] =>
  supportedHeadingOptions.filter((heading) => {
    const magnitude = Math.abs(shortestTurnDegrees(previousHeading, heading));
    return !cardinalHeadingOptions.includes(heading) && availableDeflections.includes(magnitude);
  });

const curveAngleFromTurnMagnitude = (
  magnitude: number,
  curveOptions: HrailSystemCurveOption[],
): HrailSegmentAngle | null => {
  const nominalAngle = curveOptions.find((item) => item.curve.deflectionDegrees === magnitude)?.curve
    .nominalAngleDegrees;
  return nominalAngle !== undefined && isHrailSegmentAngle(nominalAngle) ? nominalAngle : null;
};

const turnMagnitudeFromCurveAngle = (curveAngle: HrailSegmentAngle, curveOptions: HrailSystemCurveOption[]): number =>
  curveOptions.find((item) => item.curve.nominalAngleDegrees === curveAngle)?.curve.deflectionDegrees ??
  (curveAngle === 90 ? 90 : 180 - curveAngle);

const deriveCurveFromHeadings = (
  previousHeading: HrailHeadingDegrees,
  heading: HrailHeadingDegrees,
  curveOptions: HrailSystemCurveOption[],
): DerivedCurve | null => {
  const turnDegrees = shortestTurnDegrees(previousHeading, heading);
  const magnitude = Math.abs(turnDegrees);
  const curveOption = curveOptions.find((item) => item.curve.deflectionDegrees === magnitude);
  const curveAngle = curveAngleFromTurnMagnitude(magnitude, curveOptions);
  if (!curveOption || !curveAngle) {
    return null;
  }

  const isVertical = curveOption.curve.plane === "vertical";
  return {
    endAngleDegrees: curveAngle,
    turnDirection: turnDegrees > 0 ? (isVertical ? "up" : "left") : isVertical ? "down" : "right",
  };
};

const deriveHeadingFromCurve = (
  previousHeading: HrailHeadingDegrees,
  endAngleDegrees: HrailSegmentAngle,
  turnDirection: HrailTurnDirection,
  curveOptions: HrailSystemCurveOption[],
): HrailHeadingDegrees | null => {
  const magnitude = turnMagnitudeFromCurveAngle(endAngleDegrees, curveOptions);
  const sign = turnDirection === "left" || turnDirection === "up" ? 1 : -1;
  return normalizeHeading(previousHeading + sign * magnitude);
};

const routeHasResolvedCurve = (rows: DraftRouteRow[], curveOptions: HrailSystemCurveOption[]): boolean => {
  const tractRows = rows.filter((row) => row.mode !== "terminal");
  let previousHeading: HrailHeadingDegrees | null = null;

  for (const row of tractRows) {
    const heading = row.headingDegrees === "" ? null : row.headingDegrees;
    if (
      previousHeading !== null &&
      heading !== null &&
      deriveCurveFromHeadings(previousHeading, heading, curveOptions)
    ) {
      return true;
    }
    previousHeading = heading;
  }

  return false;
};

const isDoubleTerminalSupportCompatible = (supportOption: HrailSystemSupportOption | null): boolean =>
  supportOption !== null && doubleTerminalSupportCodes.has(supportOption.support.code);

const toggleCurveOrientation = (orientation: HrailCurveOrientation): HrailCurveOrientation =>
  orientation === "internal" ? "external" : "internal";

const routeHasExternal90WallCurve = (
  rows: DraftRouteRow[],
  curveOptions: HrailSystemCurveOption[],
  initialOrientation: HrailCurveOrientation | null,
): boolean => {
  if (!initialOrientation) {
    return false;
  }

  const tractRows = rows.filter((row) => row.mode !== "terminal");
  if (tractRows.length < 2) {
    return false;
  }

  const connectionCount = tractRows.length - 1;
  let orientation = initialOrientation;
  let previousTurnSign: -1 | 1 | null = null;

  for (let incomingIndex = 0; incomingIndex < connectionCount; incomingIndex += 1) {
    const outgoingIndex = incomingIndex + 1;
    const incomingHeading = tractRows[incomingIndex]?.headingDegrees;
    const outgoingHeading = tractRows[outgoingIndex]?.headingDegrees;

    if (
      incomingHeading === "" ||
      incomingHeading === undefined ||
      outgoingHeading === "" ||
      outgoingHeading === undefined
    ) {
      continue;
    }

    const signedDeflection = shortestTurnDegrees(incomingHeading, outgoingHeading);
    if (signedDeflection === 0) {
      continue;
    }

    const absoluteDeflection = Math.abs(signedDeflection);
    const wallCurve = curveOptions.find(
      (option) => option.curve.plane === "vertical" && option.curve.deflectionDegrees === absoluteDeflection,
    );
    if (!wallCurve) {
      continue;
    }

    const turnSign: -1 | 1 = signedDeflection > 0 ? 1 : -1;
    if (previousTurnSign !== null && turnSign !== previousTurnSign) {
      orientation = toggleCurveOrientation(orientation);
    }
    previousTurnSign = turnSign;

    if (absoluteDeflection === 90 && orientation === "external") {
      return true;
    }
  }

  return false;
};

const newDraftRouteSegment = (
  configurationId: string,
  mode: Exclude<RouteRowMode, "terminal">,
  headingDegrees: HrailHeadingDegrees | "" = "",
): DraftRouteRow => ({
  key: `new_${Date.now()}_${Math.random()}`,
  configurationId,
  mode,
  lengthM: 1,
  headingDegrees,
});

const newDraftTerminalRow = (configurationId: string): DraftRouteRow => ({
  key: `terminal_${Date.now()}_${Math.random()}`,
  configurationId,
  mode: "terminal",
  lengthM: "",
  headingDegrees: "",
});

const toDraftRouteRows = (
  configuration: HrailConfiguration,
  curveOptions: HrailSystemCurveOption[],
): DraftRouteRow[] => {
  const sortedSegments = [...(configuration.data?.segments ?? [])].sort((left, right) => {
    if (left.sequenceNo !== right.sequenceNo) {
      return left.sequenceNo - right.sequenceNo;
    }
    return left.id.localeCompare(right.id);
  });
  const rows: DraftRouteRow[] = [];

  sortedSegments.forEach((segment: HrailConfigurationSegment, index: number) => {
    const previousHeading = rows
      .slice()
      .reverse()
      .find((row) => row.mode !== "terminal" && row.headingDegrees !== "")?.headingDegrees;
    const inferredHeading =
      segment.headingDegrees ??
      (index === 0
        ? 0
        : previousHeading !== undefined &&
            previousHeading !== "" &&
            segment.endAngleDegrees !== null &&
            segment.turnDirection !== null
          ? deriveHeadingFromCurve(previousHeading, segment.endAngleDegrees, segment.turnDirection, curveOptions)
          : null);
    const headingDegrees = inferredHeading !== null && isHrailHeadingDegrees(inferredHeading) ? inferredHeading : "";

    rows.push({
      key: segment.id || `segment_${index}`,
      id: segment.id,
      configurationId: segment.configurationId,
      mode: index === 0 || isCardinalHeading(headingDegrees) ? "direction" : "angle",
      lengthM: segment.lengthMm / 1000,
      headingDegrees,
    });
  });

  if (rows.length === 0) {
    rows.push(newDraftRouteSegment(configuration.id, "direction", 0));
  }

  rows.push(newDraftTerminalRow(configuration.id));

  return normalizeRouteRows(rows, getCurveDeflections(curveOptions));
};

const normalizeRouteRows = (rows: DraftRouteRow[], availableDeflections: number[]): DraftRouteRow[] => {
  const normalizedRows: DraftRouteRow[] = [];
  let previousHeading: HrailHeadingDegrees | null = null;
  let terminalSeen = false;

  rows.forEach((row, index) => {
    if (terminalSeen) {
      return;
    }

    const nextRow = { ...row };
    if (index === 0) {
      nextRow.mode = "direction";
      if (!isCardinalHeading(nextRow.headingDegrees)) {
        nextRow.headingDegrees = 0;
      }
      previousHeading = nextRow.headingDegrees;
      normalizedRows.push(nextRow);
      return;
    }

    if (nextRow.mode === "terminal") {
      nextRow.headingDegrees = "";
      nextRow.lengthM = "";
      normalizedRows.push(nextRow);
      terminalSeen = true;
      return;
    }

    const options =
      previousHeading === null
        ? []
        : nextRow.mode === "angle"
          ? getCustomAngleOptions(previousHeading, availableDeflections)
          : getFixedDirectionOptions(previousHeading, availableDeflections);
    if (nextRow.headingDegrees === "" || !options.includes(nextRow.headingDegrees)) {
      nextRow.headingDegrees = options[0] ?? "";
    }
    if (nextRow.headingDegrees !== "") {
      previousHeading = nextRow.headingDegrees;
    }
    normalizedRows.push(nextRow);
  });

  if (normalizedRows.length === 0) {
    normalizedRows.push(newDraftRouteSegment("", "direction", 0));
  }
  if (!terminalSeen) {
    normalizedRows.push(newDraftTerminalRow(normalizedRows[0]?.configurationId ?? ""));
  }

  return normalizedRows;
};

const resolveInitialForm = (configuration: HrailConfiguration, settings: HrailSettings): HrailFormState => {
  const systemId = configuration.data?.system.id ?? settings.systems[0]?.id ?? "";
  const workMethodId = configuration.data?.workMethod?.id ?? getWorkMethodOptions(settings, systemId)[0]?.id ?? null;
  const substructureId =
    configuration.data?.substructure.id ?? getSubstructureOptions(settings, systemId, workMethodId)[0]?.id ?? "";
  const supportOptions = getSupportOptionOptions(settings, systemId, substructureId, workMethodId);
  const configuredSystemSupportOptionId = configuration.data?.systemSupportOption?.id ?? null;
  const systemSupportOptionId = configuredSystemSupportOptionId ?? supportOptions[0]?.id ?? null;
  const supportId =
    configuration.data?.systemSupportOption?.support.id ??
    supportOptions.find((option) => option.id === systemSupportOptionId)?.support.id ??
    null;
  const fasteningOptions = getFasteningOptionOptions(settings, systemId, substructureId, supportId);
  const configuredFasteningOptionId = configuration.data?.fasteningOption?.id ?? null;
  const fasteningOptionId =
    configuredFasteningOptionId && fasteningOptions.some((option) => option.id === configuredFasteningOptionId)
      ? configuredFasteningOptionId
      : (fasteningOptions[0]?.id ?? null);
  const trolleyOptions = getTrolleyOptionOptions(settings, systemId);
  const configuredSystemTrolleyOptionId = configuration.data?.systemTrolleyOption?.id ?? null;
  const systemTrolleyOptionId =
    configuredSystemTrolleyOptionId && trolleyOptions.some((option) => option.id === configuredSystemTrolleyOptionId)
      ? configuredSystemTrolleyOptionId
      : null;
  const systemWaterproofKitId =
    configuration.data?.systemWaterproofKit?.id ?? getWaterproofKitOptions(settings, systemId)[0]?.id ?? null;

  return {
    name: configuration.name,
    description: configuration.description ?? null,
    systemId,
    substructureId,
    workMethodId,
    systemSupportOptionId,
    fasteningOptionId,
    systemTrolleyOptionId,
    systemWaterproofKitId,
    labelLanguageId: configuration.data?.labelLanguage?.id ?? settings.labelLanguages[0]?.id ?? null,
    startTerminalType: configuration.data?.startTerminalType ?? "closed",
    endTerminalType: configuration.data?.endTerminalType ?? "closed",
    doubleTerminalType: configuration.data?.doubleTerminalType ?? "closed",
    initialCurveOrientation: configuration.data?.initialCurveOrientation ?? null,
    jointHoleTemplateRequired: configuration.data?.jointHoleTemplateRequired ?? false,
    trolleyQuantity: configuration.data?.trolleyQuantity ?? 1,
    labelQuantity: configuration.data?.labelQuantity ?? 1,
  };
};

const HRail: FC<HrailProps> = ({ configuration, isAdmin }: HrailProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const configurationsApi = useConfigurationsApi();
  const segmentsApi = useHrailConfigurationSegmentsApi();
  const itemsApi = useItemsApi();
  const { isLoading, data: settings, error } = useHrailSettings(aborter.current.signal, configuration.id);

  const [form, setForm] = useState<HrailFormState>();
  const [routeRows, setRouteRows] = useState<DraftRouteRow[]>([]);
  const [deletedSegmentIds, setDeletedSegmentIds] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string>();
  const [results, setResults] = useState<ListItemWithQuantity[]>();
  const [isSaving, setIsSaving] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    if (!settings) {
      return;
    }

    const initialForm = resolveInitialForm(configuration, settings);
    const initialCurveOptions = getSystemCurveOptions(settings, initialForm.systemId);
    setForm(initialForm);
    setRouteRows(toDraftRouteRows(configuration, initialCurveOptions));
    setDeletedSegmentIds([]);
  }, [configuration.id, configuration.data, settings]);

  const selectedSystem = useMemo(
    () => settings?.systems.find((item) => item.id === form?.systemId) ?? null,
    [settings, form?.systemId],
  );
  const curveOptions = useMemo(
    () => (settings && form ? getSystemCurveOptions(settings, form.systemId) : []),
    [settings, form?.systemId],
  );
  const workMethodOptions = useMemo(
    () => (settings && form ? getWorkMethodOptions(settings, form.systemId) : []),
    [settings, form?.systemId],
  );
  const substructureOptions = useMemo(
    () => (settings && form ? getSubstructureOptions(settings, form.systemId, form.workMethodId) : []),
    [settings, form?.systemId, form?.workMethodId],
  );
  const availableDeflections = useMemo(() => getCurveDeflections(curveOptions), [curveOptions]);
  const systemSupportOptionOptions = useMemo(
    () =>
      settings && form ? getSupportOptionOptions(settings, form.systemId, form.substructureId, form.workMethodId) : [],
    [settings, form?.systemId, form?.substructureId, form?.workMethodId],
  );
  const selectedSystemSupportOption =
    systemSupportOptionOptions.find((item) => item.id === form?.systemSupportOptionId) ?? null;
  const fasteningOptionOptions = useMemo(
    () =>
      settings && form
        ? getFasteningOptionOptions(
            settings,
            form.systemId,
            form.substructureId,
            selectedSystemSupportOption?.support.id ?? null,
          )
        : [],
    [settings, form?.systemId, form?.substructureId, selectedSystemSupportOption?.support.id],
  );
  const trolleyOptionOptions = useMemo(
    () => (settings && form ? getTrolleyOptionOptions(settings, form.systemId) : []),
    [settings, form?.systemId],
  );
  const waterproofKitOptions = useMemo(
    () => (settings && form ? getWaterproofKitOptions(settings, form.systemId) : []),
    [settings, form?.systemId],
  );
  const terminalTypeOptions = useMemo(
    () => (settings ? getTerminalTypeOptions(settings) : defaultTerminalTypeOptions),
    [settings],
  );

  const selectedWorkMethod = workMethodOptions.find((item) => item.id === form?.workMethodId) ?? null;
  const selectedSubstructure = substructureOptions.find((item) => item.id === form?.substructureId) ?? null;
  const selectedFasteningOption = fasteningOptionOptions.find((item) => item.id === form?.fasteningOptionId) ?? null;
  const selectedTrolleyOption = trolleyOptionOptions.find((item) => item.id === form?.systemTrolleyOptionId) ?? null;
  const selectedWaterproofKit = waterproofKitOptions.find((item) => item.id === form?.systemWaterproofKitId) ?? null;
  const selectedLabelLanguage = settings?.labelLanguages.find((item) => item.id === form?.labelLanguageId) ?? null;
  const doubleTerminalSelectorVisible = useMemo(
    () =>
      selectedSystem?.isDoubleTerminalVisible === true &&
      selectedSystem.mountingContext === "wall" &&
      isDoubleTerminalSupportCompatible(selectedSystemSupportOption) &&
      routeHasExternal90WallCurve(routeRows, curveOptions, form?.initialCurveOrientation ?? null),
    [selectedSystem, selectedSystemSupportOption, routeRows, curveOptions, form?.initialCurveOrientation],
  );

  const updateRouteRow = (key: string, patch: Partial<DraftRouteRow>) => {
    setRouteRows((current) =>
      normalizeRouteRows(
        current.map((row) => (row.key === key ? { ...row, ...patch } : row)),
        availableDeflections,
      ),
    );
  };

  const addRouteRow = () => {
    setRouteRows((current) => {
      const terminalIndex = current.findIndex((row) => row.mode === "terminal");
      const insertIndex = terminalIndex === -1 ? current.length : terminalIndex;
      const nextRows = [...current];
      nextRows.splice(insertIndex, 0, newDraftRouteSegment(configuration.id, "direction"));
      return normalizeRouteRows(nextRows, availableDeflections);
    });
  };

  const removeRouteRow = (key: string) => {
    const row = routeRows.find((item) => item.key === key);
    if (row?.mode === "terminal") {
      return;
    }
    if (row?.id) {
      setDeletedSegmentIds((current) => (current.includes(row.id!) ? current : [...current, row.id!]));
    }
    setRouteRows((current) =>
      normalizeRouteRows(
        current.filter((item) => item.key !== key),
        availableDeflections,
      ),
    );
  };

  const changeRouteRowMode = (key: string, mode: Exclude<RouteRowMode, "terminal">) => {
    setRouteRows((current) => {
      const rowIndex = current.findIndex((item) => item.key === key);
      if (rowIndex === -1) {
        return current;
      }

      const nextRows = [...current];
      const currentRow = nextRows[rowIndex];
      if (!currentRow || currentRow.mode === "terminal") {
        return current;
      }
      nextRows[rowIndex] = {
        ...currentRow,
        mode,
        headingDegrees: "",
        lengthM: currentRow.lengthM === "" ? 1 : currentRow.lengthM,
      };
      return normalizeRouteRows(nextRows, availableDeflections);
    });
  };

  const validate = (requireSegments: boolean): string | null => {
    if (!form || !settings) {
      return i18n.translation.common.genericError;
    }
    if (!form.systemId) {
      return "H-Rail system is required";
    }
    if (!form.substructureId) {
      return "Substructure is required";
    }
    if (!form.workMethodId) {
      return "Work method is required";
    }
    if (!form.systemSupportOptionId) {
      return "Support is required";
    }
    if (form.labelQuantity < 0) {
      return "Number of accesses / plates cannot be negative";
    }
    if (form.systemTrolleyOptionId && (!form.trolleyQuantity || form.trolleyQuantity <= 0)) {
      return "Trolley quantity must be greater than zero";
    }
    if (
      selectedSystem?.mountingContext === "wall" &&
      routeHasResolvedCurve(routeRows, curveOptions) &&
      !form.initialCurveOrientation
    ) {
      return "initialCurveOrientation is required for Wall configurations with curves";
    }
    const tractRows = routeRows.filter((row) => row.mode !== "terminal");
    if (requireSegments && tractRows.length === 0) {
      return "At least one segment is required";
    }
    if (!form.startTerminalType) {
      return "Start terminal is required";
    }
    if (!form.endTerminalType) {
      return "End terminal is required";
    }
    if (doubleTerminalSelectorVisible && !form.doubleTerminalType) {
      return "Double terminal type is required";
    }

    let previousHeading: HrailHeadingDegrees | null = null;
    let tractIndex = 0;
    let terminalSeen = false;
    for (const [rowIndex, row] of routeRows.entries()) {
      if (row.mode === "terminal") {
        if (rowIndex === 0) {
          return "Terminal cannot be the first route row";
        }
        terminalSeen = true;
        continue;
      }
      if (terminalSeen) {
        return "Terminal must be the last route row";
      }

      tractIndex += 1;
      if (row.lengthM === "" || row.lengthM <= 0) {
        return "Segment length must be greater than 0 m";
      }
      if (row.headingDegrees === "") {
        return tractIndex === 1 ? "Initial direction is required" : "Route row direction or angle is required";
      }
      if (tractIndex === 1) {
        if (!isCardinalHeading(row.headingDegrees)) {
          return "Initial direction must be a fixed direction";
        }
        previousHeading = row.headingDegrees;
        continue;
      }

      if (previousHeading === null) {
        return i18n.translation.common.genericError;
      }
      if (
        row.mode === "direction" &&
        !getFixedDirectionOptions(previousHeading, availableDeflections).includes(row.headingDegrees)
      ) {
        return "Selected fixed direction is not valid after the previous route heading";
      }
      if (
        row.mode === "angle" &&
        !getCustomAngleOptions(previousHeading, availableDeflections).includes(row.headingDegrees)
      ) {
        return "Selected custom angle is not valid after the previous route heading";
      }
      if (!deriveCurveFromHeadings(previousHeading, row.headingDegrees, curveOptions)) {
        return "Selected route row cannot be matched to an H-Rail curve";
      }
      previousHeading = row.headingDegrees;
    }

    return null;
  };

  const buildUpdateBody = (): UpdateHrailConfigurationBody | null => {
    if (!form || !selectedSystem || !selectedSubstructure) {
      return null;
    }

    return {
      systemId: selectedSystem.id,
      substructureId: selectedSubstructure.id,
      workMethodId: selectedWorkMethod?.id ?? null,
      systemSupportOptionId: selectedSystemSupportOption?.id ?? null,
      fasteningOptionId: selectedFasteningOption?.id ?? null,
      systemTrolleyOptionId: selectedTrolleyOption?.id ?? null,
      systemWaterproofKitId: selectedWaterproofKit?.id ?? null,
      labelLanguageId: selectedLabelLanguage?.id ?? null,
      startTerminalType: form.startTerminalType,
      endTerminalType: form.endTerminalType,
      doubleTerminalType: form.doubleTerminalType,
      initialCurveOrientation: selectedSystem.mountingContext === "wall" ? form.initialCurveOrientation : null,
      jointHoleTemplateRequired: selectedSystem.isJointHoleTemplateVisible && form.jointHoleTemplateRequired,
      trolleyQuantity: selectedTrolleyOption ? form.trolleyQuantity : null,
      labelQuantity: form.labelQuantity <= 0 ? 1 : form.labelQuantity,
    };
  };

  const saveSegments = async () => {
    const tractRows = routeRows.filter((row) => row.mode !== "terminal");
    const existingIds = new Set((configuration.data?.segments ?? []).map((segment) => segment.id));
    const currentIds = new Set(tractRows.map((row) => row.id).filter((id): id is string => !!id));
    const idsToDelete = new Set(deletedSegmentIds);

    for (const existingId of existingIds) {
      if (!currentIds.has(existingId)) {
        idsToDelete.add(existingId);
      }
    }

    for (const segmentId of idsToDelete) {
      if (!currentIds.has(segmentId)) {
        await segmentsApi.deleteSegment(configuration.id, segmentId);
      }
    }

    const nextRows: DraftRouteRow[] = [];
    let previousHeading: HrailHeadingDegrees | null = null;
    for (const [index, row] of tractRows.entries()) {
      if (row.lengthM === "") {
        throw new Error("Segment length is required");
      }

      const headingDegrees = row.headingDegrees === "" ? null : row.headingDegrees;
      const derivedCurve =
        previousHeading !== null && headingDegrees !== null
          ? deriveCurveFromHeadings(previousHeading, headingDegrees, curveOptions)
          : null;
      const body: UpsertHrailConfigurationSegmentBody = {
        configurationId: configuration.id,
        sequenceNo: index + 1,
        lengthMm: row.lengthM * 1000,
        headingDegrees,
        endAngleDegrees: derivedCurve?.endAngleDegrees ?? null,
        turnDirection: derivedCurve?.turnDirection ?? null,
      };

      previousHeading = headingDegrees;

      if (row.id) {
        await segmentsApi.updateSegment(configuration.id, row.id, body);
        nextRows.push(row);
      } else {
        const inserted = await segmentsApi.insertSegment(configuration.id, body);
        nextRows.push({ ...row, id: inserted.id, key: inserted.id });
      }
    }

    const terminalRow = routeRows.find((row) => row.mode === "terminal") ?? newDraftTerminalRow(configuration.id);
    setRouteRows([...nextRows, terminalRow]);
    setDeletedSegmentIds([]);
  };

  const save = async (requireSegments: boolean) => {
    const error = validate(requireSegments);
    if (error) {
      setValidationError(error);
      return false;
    }

    const hrail = buildUpdateBody();
    if (!form || !hrail || !settings) {
      setValidationError(i18n.translation.common.genericError);
      return false;
    }

    const optimisticData: HrailConfiguration = {
      ...configuration,
      name: form.name,
      description: form.description ?? undefined,
      data: {
        id: configuration.id,
        system: selectedSystem!,
        substructure: selectedSubstructure!,
        workMethod: selectedWorkMethod,
        systemSupportOption: selectedSystemSupportOption,
        fasteningOption: selectedFasteningOption,
        systemTrolleyOption: selectedTrolleyOption,
        systemWaterproofKit: selectedWaterproofKit,
        labelLanguage: selectedLabelLanguage,
        startTerminalType: hrail.startTerminalType,
        endTerminalType: hrail.endTerminalType,
        doubleTerminalType: hrail.doubleTerminalType,
        initialCurveOrientation: hrail.initialCurveOrientation,
        jointHoleTemplateRequired: hrail.jointHoleTemplateRequired,
        trolleyQuantity: hrail.trolleyQuantity,
        labelQuantity: hrail.labelQuantity,
        createdAt: configuration.data?.createdAt ?? "",
        updatedAt: configuration.data?.updatedAt ?? "",
        segments: configuration.data?.segments ?? [],
      },
    };
    const body: UpdateConfigurationBody = {
      name: form.name,
      description: form.description ?? null,
      language: configuration.language,
      hrail,
    };

    setIsSaving(true);
    setValidationError(undefined);
    try {
      await configurationsApi.updateConfiguration(configuration.project?.id, configuration.id, body, optimisticData);
      await saveSegments();
      isAdmin && dispatch(showSuccessNotification(i18n.translation));
      return true;
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const calculate = async () => {
    if (configuration.canEdit) {
      const saved = await save(true);
      if (!saved) {
        return;
      }
    } else {
      const error = validate(true);
      if (error) {
        setValidationError(error);
        return;
      }
    }

    setIsCalculating(true);
    setValidationError(undefined);
    try {
      const items = await itemsApi.calculateConfigurationItemsList(configuration.id, aborter.current.signal);
      setResults(items.filter((item: ListItemWithQuantity) => item.quantity > 0));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsCalculating(false);
    }
  };

  if (isLoading || (!settings && !error)) {
    return (
      <FlexContent padding>
        <LoadingSpinner />
      </FlexContent>
    );
  }

  if (error || !settings || !form) {
    return (
      <FlexContent padding>
        <ErrorText text={i18n.translation.common.genericError} />
      </FlexContent>
    );
  }

  const disabled = isSaving || !configuration.canEdit;
  return (
    <FlexContent padding>
      <div className={styles.container}>
        {validationError && <ErrorText text={validationError} />}

        <section className={styles.section}>
          <h2>Configuration</h2>
          <div className={styles.grid}>
            <FormField label={i18n.translation.common.name}>
              {({ labelId }) => (
                <Input
                  id={labelId}
                  value={form.name}
                  disabled={disabled}
                  onChange={(event) => setForm({ ...form, name: event.currentTarget.value })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.description} isOptional>
              {({ labelId }) => (
                <Textarea
                  id={labelId}
                  value={form.description ?? ""}
                  disabled={disabled}
                  onChange={(event) => setForm({ ...form, description: event.currentTarget.value || null })}
                />
              )}
            </FormField>
          </div>
        </section>

        <section className={styles.section}>
          <h2>H-Rail</h2>
          <div className={styles.grid}>
            <FormField label="System">
              {({ labelId }) => (
                <CustomSelect<HrailSystem>
                  inputId={labelId}
                  value={selectedSystem}
                  options={settings.systems}
                  isDisabled={disabled}
                  getOptionLabel={(option) => `${option.code} - ${option.name}`}
                  getOptionValue={(option) => option.id}
                  onChange={(value) => {
                    const nextSystemId = value?.id ?? "";
                    if (nextSystemId === form.systemId) {
                      return;
                    }
                    const nextCurveDeflections = getCurveDeflections(getSystemCurveOptions(settings, nextSystemId));
                    setRouteRows((current) => normalizeRouteRows(current, nextCurveDeflections));
                    setForm({
                      ...form,
                      systemId: nextSystemId,
                      workMethodId: null,
                      substructureId: "",
                      systemSupportOptionId: null,
                      fasteningOptionId: null,
                      systemTrolleyOptionId: null,
                      systemWaterproofKitId: null,
                      doubleTerminalType: null,
                      jointHoleTemplateRequired: false,
                      initialCurveOrientation: value?.mountingContext === "wall" ? form.initialCurveOrientation : null,
                    });
                  }}
                />
              )}
            </FormField>

            <FormField label="Work method">
              {({ labelId }) => (
                <CustomSelect<HrailWorkMethod>
                  inputId={labelId}
                  value={selectedWorkMethod}
                  options={workMethodOptions}
                  isDisabled={disabled || !form.systemId || workMethodOptions.length === 0}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  onChange={(value) => {
                    const nextWorkMethodId = value?.id ?? null;
                    if (nextWorkMethodId === form.workMethodId) {
                      return;
                    }
                    const nextSubstructures = getSubstructureOptions(settings, form.systemId, nextWorkMethodId);
                    const nextSubstructureId = nextSubstructures.some(
                      (substructure) => substructure.id === form.substructureId,
                    )
                      ? form.substructureId
                      : (nextSubstructures[0]?.id ?? "");
                    const nextSupportOptions = getSupportOptionOptions(
                      settings,
                      form.systemId,
                      nextSubstructureId,
                      nextWorkMethodId,
                    );
                    setForm({
                      ...form,
                      workMethodId: nextWorkMethodId,
                      substructureId: nextSubstructureId,
                      systemSupportOptionId: retainCompatibleSupportOptionId(
                        form.systemSupportOptionId,
                        nextSupportOptions,
                      ),
                      fasteningOptionId: null,
                    });
                  }}
                />
              )}
            </FormField>

            <FormField label="Substructure">
              {({ labelId }) => (
                <CustomSelect<HrailSubstructure>
                  inputId={labelId}
                  value={selectedSubstructure}
                  options={substructureOptions}
                  isDisabled={disabled || !form.systemId || !form.workMethodId || substructureOptions.length === 0}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  onChange={(value) => {
                    const nextSubstructureId = value?.id ?? "";
                    if (nextSubstructureId === form.substructureId) {
                      return;
                    }
                    const nextSupportOptions = getSupportOptionOptions(
                      settings,
                      form.systemId,
                      nextSubstructureId,
                      form.workMethodId,
                    );
                    setForm({
                      ...form,
                      substructureId: nextSubstructureId,
                      systemSupportOptionId: retainCompatibleSupportOptionId(
                        form.systemSupportOptionId,
                        nextSupportOptions,
                      ),
                      fasteningOptionId: null,
                    });
                  }}
                />
              )}
            </FormField>

            <FormField label="Support">
              {({ labelId }) => (
                <CustomSelect<HrailSystemSupportOption>
                  inputId={labelId}
                  value={selectedSystemSupportOption}
                  options={systemSupportOptionOptions}
                  isDisabled={disabled || systemSupportOptionOptions.length === 0}
                  getOptionLabel={(option) => option.support.code}
                  getOptionValue={(option) => option.id}
                  onChange={(value) => {
                    const nextSystemSupportOptionId = value?.id ?? null;
                    if (nextSystemSupportOptionId === form.systemSupportOptionId) {
                      return;
                    }
                    setForm({
                      ...form,
                      systemSupportOptionId: nextSystemSupportOptionId,
                      fasteningOptionId: null,
                    });
                  }}
                />
              )}
            </FormField>

            <FormField label="Fastening option" isOptional>
              {({ labelId }) => (
                <CustomSelect<HrailFasteningOption>
                  inputId={labelId}
                  value={selectedFasteningOption}
                  options={fasteningOptionOptions}
                  isDisabled={disabled || fasteningOptionOptions.length === 0}
                  isClearable
                  getOptionLabel={(option) =>
                    option.itemSignature ||
                    option.items.map((item) => item.item.code).join(", ") ||
                    option.substructure.name
                  }
                  getOptionValue={(option) => option.id}
                  onChange={(value) => setForm({ ...form, fasteningOptionId: value?.id ?? null })}
                />
              )}
            </FormField>

            <FormField label="Trolley" isOptional>
              {({ labelId }) => (
                <CustomSelect<HrailSystemTrolleyOption>
                  inputId={labelId}
                  value={selectedTrolleyOption}
                  options={trolleyOptionOptions}
                  isDisabled={disabled || trolleyOptionOptions.length === 0}
                  isClearable
                  getOptionLabel={(option) =>
                    itemLabel(option.trolley.item.code, option.trolley.item.description) || option.trolley.item.code
                  }
                  getOptionValue={(option) => option.id}
                  onChange={(value) => {
                    const nextSystemTrolleyOptionId = value?.id ?? null;
                    if (nextSystemTrolleyOptionId === form.systemTrolleyOptionId) {
                      return;
                    }
                    setForm({ ...form, systemTrolleyOptionId: nextSystemTrolleyOptionId });
                  }}
                />
              )}
            </FormField>

            {selectedTrolleyOption && (
              <FormField label="Trolley quantity">
                {({ labelId }) => (
                  <Input
                    id={labelId}
                    type="number"
                    min={1}
                    value={form.trolleyQuantity ?? ""}
                    disabled={disabled}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        trolleyQuantity: event.currentTarget.value === "" ? null : Number(event.currentTarget.value),
                      })
                    }
                  />
                )}
              </FormField>
            )}

            <FormField label="Waterproof kit" isOptional>
              {({ labelId }) => (
                <CustomSelect<HrailSystemWaterproofKit>
                  inputId={labelId}
                  value={selectedWaterproofKit}
                  options={waterproofKitOptions}
                  isDisabled={disabled || waterproofKitOptions.length === 0}
                  isClearable
                  getOptionLabel={(option) =>
                    itemLabel(option.waterproofKit.item.code, option.waterproofKit.item.description) ||
                    option.waterproofKit.item.code
                  }
                  getOptionValue={(option) => option.id}
                  onChange={(value) => setForm({ ...form, systemWaterproofKitId: value?.id ?? null })}
                />
              )}
            </FormField>

            <FormField label="Label language" isOptional>
              {({ labelId }) => (
                <CustomSelect<HrailLabelLanguage>
                  inputId={labelId}
                  value={selectedLabelLanguage}
                  options={settings.labelLanguages}
                  isDisabled={disabled}
                  isClearable
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  onChange={(value) => setForm({ ...form, labelLanguageId: value?.id ?? null })}
                />
              )}
            </FormField>

            <FormField label="Number of accesses / plates">
              {({ labelId }) => (
                <Input
                  id={labelId}
                  type="number"
                  min={0}
                  value={form.labelQuantity}
                  disabled={disabled}
                  onChange={(event) => setForm({ ...form, labelQuantity: Number(event.currentTarget.value) })}
                />
              )}
            </FormField>

            {selectedSystem?.isJointHoleTemplateVisible && (
              <FormField label="Joint hole template" isOptional>
                {({ labelId }) => (
                  <Switch
                    id={labelId}
                    checked={form.jointHoleTemplateRequired}
                    disabled={disabled}
                    onChange={(checked) => setForm({ ...form, jointHoleTemplateRequired: checked })}
                  />
                )}
              </FormField>
            )}

            {selectedSystem?.mountingContext === "wall" && (
              <FormField label="Initial curve orientation" isOptional>
                {({ labelId }) => (
                  <select
                    id={labelId}
                    className={styles.select}
                    value={form.initialCurveOrientation ?? ""}
                    disabled={disabled}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        initialCurveOrientation:
                          event.currentTarget.value === ""
                            ? null
                            : (event.currentTarget.value as HrailCurveOrientation),
                      })
                    }
                  >
                    <option value=""></option>
                    {curveOrientationOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              </FormField>
            )}

            {doubleTerminalSelectorVisible && (
              <FormField label={i18n.translation.common.doubleTerminal}>
                {({ labelId }) => (
                  <select
                    id={labelId}
                    className={styles.select}
                    value={form.doubleTerminalType ?? ""}
                    disabled={disabled}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        doubleTerminalType:
                          event.currentTarget.value === "" ? null : (event.currentTarget.value as HrailTerminalType),
                      })
                    }
                  >
                    <option value=""></option>
                    {terminalTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              </FormField>
            )}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2>Route</h2>
            <Button glyph={SvgAdd} type="secondary" buttonProps={{ disabled, onClick: addRouteRow }}>
              Add
            </Button>
          </div>

          <div className={styles.routeTerminals}>
            <FormField label="Start terminal">
              {({ labelId }) => (
                <select
                  id={labelId}
                  className={styles.select}
                  value={form.startTerminalType}
                  disabled={disabled}
                  onChange={(event) =>
                    setForm({ ...form, startTerminalType: event.currentTarget.value as HrailTerminalType })
                  }
                >
                  {terminalTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </FormField>
          </div>

          <div className={styles.tableWrapper}>
            <table className={styles.segmentTable}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Direction</th>
                  <th>Angle</th>
                  <th>Length (m)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {routeRows.map((row, rowIndex) => {
                  const previousHeading = routeRows
                    .slice(0, rowIndex)
                    .reverse()
                    .find((item) => item.mode !== "terminal" && item.headingDegrees !== "")?.headingDegrees;
                  const directionOptions =
                    rowIndex === 0
                      ? cardinalHeadingOptions
                      : previousHeading !== undefined && previousHeading !== ""
                        ? getFixedDirectionOptions(previousHeading, availableDeflections)
                        : [];
                  const customAngleOptions =
                    previousHeading !== undefined && previousHeading !== ""
                      ? getCustomAngleOptions(previousHeading, availableDeflections)
                      : [];
                  const tractRowsCount = routeRows.filter((item) => item.mode !== "terminal").length;

                  return (
                    <tr key={row.key}>
                      <td>{rowIndex + 1}</td>
                      <td>
                        {rowIndex === 0 ? (
                          "Start"
                        ) : row.mode === "terminal" ? (
                          "End"
                        ) : (
                          <select
                            className={styles.select}
                            value={row.mode}
                            disabled={disabled}
                            onChange={(event) =>
                              changeRouteRowMode(
                                row.key,
                                event.currentTarget.value as Exclude<RouteRowMode, "terminal">,
                              )
                            }
                          >
                            {routeRowModeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        {row.mode === "terminal" ? (
                          <select
                            className={styles.select}
                            value={form.endTerminalType}
                            disabled={disabled}
                            onChange={(event) =>
                              setForm({ ...form, endTerminalType: event.currentTarget.value as HrailTerminalType })
                            }
                          >
                            {terminalTypeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <select
                            className={styles.select}
                            value={row.mode === "direction" ? row.headingDegrees : ""}
                            disabled={disabled || row.mode !== "direction"}
                            onChange={(event) =>
                              updateRouteRow(row.key, {
                                headingDegrees:
                                  event.currentTarget.value === ""
                                    ? ""
                                    : (Number(event.currentTarget.value) as HrailHeadingDegrees),
                              })
                            }
                          >
                            <option value=""></option>
                            {directionOptions.map((option) => (
                              <option key={option} value={option}>
                                {headingLabel(option)}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td>
                        <select
                          className={styles.select}
                          value={row.mode === "angle" ? row.headingDegrees : ""}
                          disabled={disabled || row.mode !== "angle"}
                          onChange={(event) =>
                            updateRouteRow(row.key, {
                              headingDegrees:
                                event.currentTarget.value === ""
                                  ? ""
                                  : (Number(event.currentTarget.value) as HrailHeadingDegrees),
                            })
                          }
                        >
                          <option value=""></option>
                          {customAngleOptions.map((option) => (
                            <option key={option} value={option}>
                              {headingLabel(option)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {row.mode !== "terminal" && (
                          <Input
                            type="number"
                            min={0.01}
                            step={0.01}
                            placeholder="Length in m"
                            value={row.lengthM}
                            disabled={disabled}
                            onChange={(event) => {
                              const value = event.currentTarget.value;
                              if (value === "") {
                                updateRouteRow(row.key, { lengthM: "" });
                                return;
                              }

                              const lengthM = Number(value);
                              updateRouteRow(row.key, { lengthM: Number.isFinite(lengthM) ? lengthM : "" });
                            }}
                          />
                        )}
                      </td>
                      <td>
                        <Button
                          glyph={SvgDelete}
                          type="secondary"
                          buttonProps={{
                            disabled: disabled || row.mode === "terminal" || (rowIndex === 0 && tractRowsCount === 1),
                            onClick: () => removeRouteRow(row.key),
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <SubmitBar>
          <Button
            glyph={SvgSave}
            type="secondary"
            buttonProps={{
              disabled: isSaving || !configuration.canEdit,
              onClick: () => save(false),
            }}
          >
            Save
          </Button>
          <Button buttonProps={{ disabled: isSaving || isCalculating, onClick: calculate }}>Calculate</Button>
        </SubmitBar>

        {results && (
          <section className={styles.section}>
            <h2>Results</h2>
            <Table<ListItemWithQuantity>
              head={
                <tr>
                  <th align="left">{i18n.translation.common.code}</th>
                  <th align="left">{i18n.translation.common.description}</th>
                  <th align="right">{i18n.translation.common.qty}</th>
                </tr>
              }
              items={results}
              renderRow={(item) => (
                <>
                  <td>{item.code}</td>
                  <td>{item.description}</td>
                  <td align="right">{item.quantity}</td>
                </>
              )}
            />
          </section>
        )}
      </div>
    </FlexContent>
  );
};

export default HRail;
